import styles from "./selectInput.module.scss";
import formStyles from "../formStyles.module.scss";
import { SelectOptions } from "@lib/types";
/**
 * 
 * Select dropdown with an associated label for accessible form entry.
 * 
 * @remarks
 * This is a controlled input. The selected value is managed via the 'value' prop and updated with 'onChange'.
 * The 'onChange' callback provides the current selected value and the native change event for flexibility. You can then use the value to update your form state as needed or run any other function onchange.
 * Additional native <select> props (e.g. multiple, size) can be passed via ...rest.
 * 
 * If you provide 'helpText' or 'errorMessage', they are programmatically associated via 'aria-describedby'.
 * Providing an 'errorMessage' marks the field invalid ('aria-invalid') and renders the error text.
 * When 'showRequired' is false, the input remains accessible via 'aria-required'.
 * 
 * @param props.id - Unique input ID used to associate the label
 * @param props.name - Field name used for form submission/validation
 * @param props.title - Visible label text
 * @param props.options - Array of { label, value } objects for select options
 * @param props.value - Current selected value of the input
 * @param props.onChange Optional - (value, e?) => {someFunc(value)}. Value is the current selected value, e is the native change event if you need it. SomeFunc is the function you want to call with the new value.
 * @param props.onBlur Optional - (value, e?) => {someFunc(value)}. Value is the current selected value, e is the native blur event if you need it. SomeFunc is the function you want to call with the new value on blur.
 * @param props.required Optional - Marks the field required (default: false)
 * @param props.showRequired Optional - Controls whether the required asterisk (*) is shown
 * @param props.helpText Optional - Helper text shown under the label
 * @param props.errorMessage Optional - Error message shown under the input
 * @param props.disabled Optional - Disables the input
 * @param props.formInstructionsId Optional - ID for additional accessibility description
 * @param props.rest - Any additional native <select> attributes
 * 
 * @example
 * ```tsx
 * <SelectInput
 *   id="country"
 *   name="country"
 *   title="Country"
 *   options={[
 *     { label: 'Australia', value: 'au' },
 *     { label: 'New Zealand', value: 'nz' },
 *   ]}
 *   value={form.country}
 *   onChange={(countryValue) => setForm({ ...form, country: countryValue })}
 * />
 * ```
 */
const SelectInput = ({
    id,
    name,
    title,
    options,
    value,
    onChange,
    onBlur,
    required = false,
    showRequired = true,
    helpText,
    errorMessage,
    disabled,
    formInstructionsId,
    ...rest
}: {
    id: string;
    name: string;
    title: string;
    options: SelectOptions;
    value: string;
    onChange?: (value: string, e?: React.ChangeEvent<HTMLSelectElement>) => void;
    onBlur?: (value: string, e?: React.FocusEvent<HTMLSelectElement>) => void;
    required?: boolean;
    showRequired?: boolean;
    helpText?: string;
    errorMessage?: string;
    disabled?: boolean;
    formInstructionsId?: string;
} & Omit<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    'onChange' | 'onBlur' | 'value'
>) => {
    const helpId = `${id}-help`;
    const errorId = `${id}-error`;
    const asterisk = (
        <span
            aria-hidden={!showRequired}
            className={formStyles.requiredAsterisk}
        >
            {' '}
            *
        </span>
    );
    const describedBy = [
        helpText ? helpId : null,
        errorMessage ? errorId : null,
        formInstructionsId || null,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            data-testid="selectinput"
            className={formStyles.inputcontainer}
        >
            <label
                data-testid="selectinputlabel"
                htmlFor={id}
                className={formStyles.label}
            >
                {title}
                {required && showRequired ? asterisk : ''}
            </label>

            {helpText && (
                <p id={helpId} className={formStyles.help}>
                    {helpText}
                </p>
            )}

            <select
                data-testid="selectinputselect"
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange?.(e.target.value, e)}
                onBlur={(e) => onBlur?.(e.target.value, e)}
                required={required}
                disabled={disabled}
                aria-invalid={Boolean(errorMessage) || undefined}
                aria-describedby={describedBy || undefined}
                aria-required={required || undefined}
                className={`${formStyles.input} ${rest.className ?? ''} ${styles.arrow}`}
                {...rest}
            >
                {options.map(({ label, value }) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </select>

            {errorMessage && (
                <p id={errorId} className={formStyles.error} role="alert">
                    {errorMessage}
                </p>
            )}
        </div>
    );
};

export default SelectInput;